import {
  createAsyncThunk,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit"

import * as plansSowApi from "../api/services.ts"
import { type SowDocument } from "../api/services.types.ts"

interface PlansSowState {
  activeDocument: SowDocument | null
  inbox: SowDocument[]
  inboxCount: number
  status: "idle" | "loading" | "failed"
}

const initialState: PlansSowState = {
  activeDocument: null,
  inbox: [],
  inboxCount: 0,
  status: "idle"
}

interface CreateAndUploadResult {
  document: SowDocument
  pageCount: number
}

// A single thunk for "create the document, then upload its plan source" —
// the two backend calls (POST /plans-sow/ then POST /plans-sow/{id}/source/)
// are one user action (picking a file in UploadScreen) and always happen
// together, so there's no reason for a caller to sequence them itself.
// uploadPlanSource's own response only carries { id, page_count } (no full
// document), so the document is re-fetched once both calls succeed to give
// the caller a complete, current SowDocument alongside the page count.
export const createAndUploadPlanThunk = createAsyncThunk(
  "plansSow/createAndUpload",
  async ({
    title,
    file
  }: {
    title: string
    file: File
  }): Promise<CreateAndUploadResult> => {
    const created = await plansSowApi.createDocument(title)
    const { page_count: pageCount } = await plansSowApi.uploadPlanSource(
      created.id,
      file
    )
    const document = await plansSowApi.getDocument(created.id)
    return { document, pageCount }
  }
)

export const fetchDocument = createAsyncThunk(
  "plansSow/fetch",
  async (id: string) => plansSowApi.getDocument(id)
)

export const fetchInbox = createAsyncThunk("plansSow/fetchInbox", async () =>
  plansSowApi.listDocuments()
)

const plansSowSlice = createSlice({
  name: "plansSow",
  initialState,
  reducers: {
    // Used by this slice's own test, and available to any screen that
    // already holds a fresh document and wants to seed activeDocument
    // without a redundant re-fetch.
    setActiveDocumentForTest(state, action: PayloadAction<SowDocument>) {
      state.activeDocument = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAndUploadPlanThunk.fulfilled, (state, action) => {
        state.activeDocument = action.payload.document
      })
      .addCase(fetchDocument.fulfilled, (state, action) => {
        state.activeDocument = action.payload
      })
      .addCase(fetchInbox.pending, (state) => {
        state.status = "loading"
      })
      .addCase(fetchInbox.fulfilled, (state, action) => {
        state.inbox = action.payload.results
        state.inboxCount = action.payload.count
        state.status = "idle"
      })
      .addCase(fetchInbox.rejected, (state) => {
        state.status = "failed"
      })
  }
})

export const { setActiveDocumentForTest } = plansSowSlice.actions
export default plansSowSlice.reducer
